import { BULK_MESSAGE_PROPOSAL } from "@/api/queries/bulkMessageProposal";
import {
  ProposeBulkMessageQuery,
  ProposeBulkMessageCsvQuery,
  BulkMessageFilterInput,
  Student,
} from "@/__generated__/graphql";
import { useQuery } from "@apollo/client";

interface ProposeBulkMessageProps {
  orgId?: string;
  filters: BulkMessageFilterInput;
}

interface ProposeBulkMessageResult {
  students: Student[];
  missingStudentIds?: number[];
}

const IMAGE_SIZE = 48;

export function useBulkMessageProposal({
  orgId,
  filters,
}: ProposeBulkMessageProps) {
  const { data, loading, error } = useQuery(BULK_MESSAGE_PROPOSAL, {
    variables: {
      orgId: orgId ?? "",
      imageSize: IMAGE_SIZE,
      filters: filters ?? {},
    },
    skip: !orgId || !filtersPresent(filters),
  });

  return { loading, error, ...processBulkMessageProposal(data) };
}

export function processBulkMessageProposal(
  data?: ProposeBulkMessageCsvQuery | ProposeBulkMessageQuery,
): ProposeBulkMessageResult {
  const bulkMessageProposalResults: ProposeBulkMessageResult = {
    students: [],
    missingStudentIds: [],
  };
  const node = data?.node;
  if (node?.__typename === "Organization") {
    const results = node.bulkMessageProposal;

    if ("students" in results) {
      const students =
        results?.students?.edges?.map((e) => {
          return {
            id: e?.node?.id ?? "",
            uuid: e?.node?.uuid ?? "",
            firstName: e?.node?.firstName ?? "",
            lastName: e?.node?.lastName ?? "",
            name: [e?.node?.firstName, e?.node?.lastName]
              .filter((name) => !!name)
              .join(" "),
            profilePhoto: e?.node?.profilePhoto,
          };
        }) || [];

      bulkMessageProposalResults.students = students;
    }
    if ("missingStudentIds" in results) {
      bulkMessageProposalResults.missingStudentIds =
        results?.missingStudentIds ?? [];
    }
  }
  return bulkMessageProposalResults;
}

const filtersPresent = (filters: BulkMessageFilterInput) =>
  Object.values(filters ?? {}).some(
    (value) =>
      value &&
      (Array.isArray(value)
        ? value.filter((x) => !!x)?.length
        : !!value.rise.actionDetails.riseUserActionId),
  );
